import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IMPERSONATION_STORAGE_KEY } from '../utils/constants';
import type { ImpersonationTarget } from '../types/impersonation';

function getStoredTarget(): ImpersonationTarget | null {
  try {
    const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function useImpersonation() {
  const [targetUser, setTargetUser] = useState<ImpersonationTarget | null>(
    getStoredTarget
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const startImpersonation = useCallback(
    (user: ImpersonationTarget) => {
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(user));
      setTargetUser(user);
      // Drop (not invalidate) the cache. On a direct customer-to-customer
      // switch, invalidateQueries would background-refetch while STALE data
      // from the PREVIOUS customer is still rendered under the new
      // customer's banner — a mislabeled-data hazard worse than a spinner.
      // removeQueries clears the cache so freshly mounted pages fetch clean
      // with the new X-Impersonate-User header.
      queryClient.removeQueries();
    },
    [queryClient]
  );

  const stopImpersonation = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setTargetUser(null);
    // Navigate first, then drop the cache. Still-mounted queries (e.g. a
    // still-mounted BillingPage) must not refetch as the real (sa) user
    // without the impersonation header before navigation lands — that
    // refetch hits portal-api as sa, gets a 403 from requireRole(), and
    // flashes an error UI. removeQueries (not invalidateQueries) clears the
    // cache without triggering a refetch of those still-mounted queries;
    // whatever page we navigate to fetches clean afterward. This
    // intentionally also drops the ['user'] query, so the SA's own identity
    // is refetched clean on /services rather than the observer continuing
    // to show the impersonated customer's stale identity — a brief blank
    // header is preferable to a mislabeled one.
    navigate('/services');
    queryClient.removeQueries();
  }, [queryClient, navigate]);

  return {
    isImpersonating: targetUser !== null,
    targetUser,
    startImpersonation,
    stopImpersonation,
  };
}

// Standalone function for use in API client (outside React context)
export function getImpersonationTarget(): ImpersonationTarget | null {
  return getStoredTarget();
}
